import React, { useEffect, useState } from "react";
import MathText from "./MathText";
import "./launch-pages.css";
const API_URL = "https://api.brainteaserday.com";
const themes = {
  general: "Général",
  logique: "Logique",
  probabilites: "Probabilités",
  geometrie: "Géométrie",
  finance: "Finance",
  arithmetique: "Arithmétique",
};
const dateLabel = (day) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(day + "T12:00:00Z"));
export default function ArchivePage() {
  const [rows, setRows] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [search, setSearch] = useState(""),
    [theme, setTheme] = useState(""),
    [day, setDay] = useState(
      () => new URLSearchParams(window.location.search).get("day") || "",
    );
  const [offset, setOffset] = useState(0),
    [hasMore, setHasMore] = useState(false),
    [retry, setRetry] = useState(0),
    [query, setQuery] = useState("");
  const limit = 12;
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      theme,
      day,
      search: query,
    });
    fetch(API_URL + "/api/archive?" + params, { signal: controller.signal })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw Error(data.error || "Archives indisponibles.");
        return data;
      })
      .then((data) => {
        setRows(data.rows || []);
        setHasMore(Boolean(data.hasMore));
      })
      .catch((e) => {
        if (e.name !== "AbortError")
          setError(
            "Les archives ne sont pas disponibles pour le moment. Réessaie dans un instant.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [offset, theme, day, query, retry]);
  function reset() {
    setSearch("");
    setQuery("");
    setTheme("");
    setDay("");
    setOffset(0);
  }
  return (
    <main className="page-container archive-page fade-in">
      <header className="archive-hero">
        <div>
          <span className="section-tag">LA BIBLIOTHÈQUE DES DÉFIS</span>
          <h1>Un déclic à retrouver.</h1>
          <p>
            Reprends une ancienne énigme. Prends le temps de chercher, puis
            découvre le raisonnement.
          </p>
        </div>
        <span className="archive-symbol" aria-hidden="true">
          ∑
        </span>
      </header>
      <section
        className="card archive-filters"
        aria-label="Rechercher une énigme"
      >
        <label>
          Une idée, un mot-clé
          <input
            type="search"
            className="input"
            placeholder="Dés, triangle, pourcentage…"
            value={search}
            maxLength={120}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label>
          Thème
          <select
            className="input"
            value={theme}
            onChange={(e) => {
              setTheme(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Tous les thèmes</option>
            {Object.entries(themes).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Jour du défi
          <input
            type="date"
            className="input"
            value={day}
            onChange={(e) => {
              setDay(e.target.value);
              setOffset(0);
            }}
          />
        </label>
        {(search || theme || day) && (
          <button className="btn" onClick={reset}>
            Effacer les filtres
          </button>
        )}
      </section>
      <div className="archive-section-heading">
        <h2>{theme ? themes[theme] : "Les défis passés"}</h2>
        <span>Les solutions du jour restent secrètes.</span>
      </div>
      {loading ? (
        <div className="card archive-empty" role="status">
          On ouvre les archives…
        </div>
      ) : error ? (
        <div className="card archive-empty" role="alert">
          <p>{error}</p>
          <button className="btn" onClick={() => setRetry((v) => v + 1)}>
            Réessayer
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card archive-empty">
          <h2>Aucune énigme ici pour le moment.</h2>
          <p>Essaie un autre mot-clé ou élargis les filtres.</p>
          <button className="btn" onClick={reset}>
            Voir toutes les archives
          </button>
        </div>
      ) : (
        <div className="archive-grid">
          {rows.map((r) => (
            <article
              key={r.day_key + "-" + r.theme}
              className="card archive-card"
            >
              <div className="archive-card-meta">
                <span className="archive-theme">
                  {themes[r.theme] || "Autre thème"}
                </span>
                <time dateTime={r.day_key}>{dateLabel(r.day_key)}</time>
              </div>
              <div className="archive-question">
                <MathText>{r.question}</MathText>
              </div>
              <details className="archive-solution">
                <summary>
                  Découvrir la solution <span aria-hidden="true">↗</span>
                </summary>
                <div className="archive-answer">
                  <span>La réponse</span>
                  <strong>
                    <MathText>{r.answer}</MathText>
                  </strong>
                </div>
                <h3>Le raisonnement</h3>
                <MathText>
                  {r.explanation ||
                    "La correction détaillée n’est pas encore disponible pour cette énigme."}
                </MathText>
              </details>
            </article>
          ))}
        </div>
      )}
      {!loading && !error && (offset > 0 || hasMore) && (
        <nav className="archive-pagination" aria-label="Pages des archives">
          <button
            className="btn"
            disabled={offset === 0}
            onClick={() => setOffset((v) => Math.max(0, v - limit))}
          >
            ← Plus récents
          </button>
          <span>Page {Math.floor(offset / limit) + 1}</span>
          <button
            className="btn"
            disabled={!hasMore}
            onClick={() => setOffset((v) => v + limit)}
          >
            Plus anciens →
          </button>
        </nav>
      )}
    </main>
  );
}
